/**
 * Centralized cache configuration
 * All cache durations and settings in one place
 */

/**
 * Cache durations in milliseconds
 * Use these constants throughout the application for consistency
 */
export const CACHE_DURATIONS = {
  /** 1 minute - for frequently changing data like real-time stats */
  SHORT: 1 * 60 * 1000,

  /** 5 minutes - default for most API calls */
  MEDIUM: 5 * 60 * 1000,

  /** 15 minutes - for data that changes occasionally */
  LONG: 15 * 60 * 1000,

  /** 1 hour - for relatively static data */
  VERY_LONG: 60 * 60 * 1000,

  /** 24 hours - for mostly static data like configurations */
  DAY: 24 * 60 * 60 * 1000,

  /** 7 days - for very static data */
  WEEK: 7 * 24 * 60 * 60 * 1000,
} as const;

/**
 * Service-specific cache durations
 * Map service types to their appropriate cache durations
 */
export const SERVICE_CACHE_DURATIONS = {
  // Catalog services
  artworks: CACHE_DURATIONS.MEDIUM,
  exhibitions: CACHE_DURATIONS.LONG,
  installations: CACHE_DURATIONS.LONG,
  immersives: CACHE_DURATIONS.LONG,
  writings: CACHE_DURATIONS.VERY_LONG,
  physicals: CACHE_DURATIONS.MEDIUM,
  collaborations: CACHE_DURATIONS.MEDIUM,
  nftcollection: CACHE_DURATIONS.SHORT,

  // Analytics services
  analytics: CACHE_DURATIONS.SHORT,
  leaderboard: CACHE_DURATIONS.SHORT,
  events: CACHE_DURATIONS.SHORT,

  // Content services
  press: CACHE_DURATIONS.VERY_LONG,
  newsletter: CACHE_DURATIONS.DAY,
  meta: CACHE_DURATIONS.MEDIUM,

  // User services
  collector: CACHE_DURATIONS.MEDIUM,
  user: CACHE_DURATIONS.MEDIUM,
  auth: CACHE_DURATIONS.SHORT,
} as const;

/**
 * Cache refresh settings
 */
export const CACHE_SETTINGS = {
  /** When to start refreshing cache before expiry (1 minute) */
  REFRESH_THRESHOLD: 60 * 1000,

  /** Maximum cache size in MB (optional, for future implementation) */
  MAX_CACHE_SIZE_MB: 50,

  /** Enable/disable cache in development */
  ENABLE_IN_DEV: false,

  /** Enable background refresh */
  ENABLE_BACKGROUND_REFRESH: true,
} as const;

/**
 * Get cache duration for a specific service
 */
export function getCacheDuration(service: keyof typeof SERVICE_CACHE_DURATIONS): number {
  return SERVICE_CACHE_DURATIONS[service] || CACHE_DURATIONS.MEDIUM;
}

/**
 * Check if caching is enabled based on environment
 */
export function isCacheEnabled(): boolean {
  if (process.env.NODE_ENV === 'development') {
    return CACHE_SETTINGS.ENABLE_IN_DEV;
  }
  return true;
}

/**
 * Format cache duration for display
 */
export function formatCacheDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days} day${days > 1 ? 's' : ''}`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
  if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  return `${seconds} second${seconds > 1 ? 's' : ''}`;
}
